// ─────────────────────────────────────────────────────────────────────────────
// ContactPage.jsx
// All content pulled from companyInfo.js — edit there, updates everywhere
//
// ─────────────────────────────────────────────────────────────────────────────

import { useState }       from 'react'
import PageHero           from '@components/common/PageHero'
import Button             from '@components/common/Button'
import CtaBanner          from '@components/common/CtaBanner'
import { companyInfo }    from '@data/companyInfo'
import SEOHead            from '@components/common/SEOHead'

// ── Social icons map ──────────────────────────────────────────────────────────
const SOCIAL_ICONS = {
  facebook: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
    </svg>
  ),
  instagram: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="2" y="2" width="20" height="20" rx="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"/>
    </svg>
  ),
  linkedin: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
      <circle cx="4" cy="4" r="2"/>
    </svg>
  ),
  whatsapp: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.859L.057 23.478a.75.75 0 00.916.919l5.701-1.464A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.695 9.695 0 01-4.953-1.355l-.355-.211-3.683.945.973-3.596-.231-.37A9.718 9.718 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/>
    </svg>
  ),
}

// ── Small info row (icon + label + value) ─────────────────────────────────────
function InfoRow({ icon, label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '20px' }}>
      <div style={{
        width: '38px', height: '38px', flexShrink: 0,
        background: 'rgba(0,121,53,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#007935',
      }}>
        {icon}
      </div>
      <div>
        <p style={{
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '10.5px', fontWeight: 700,
          letterSpacing: '0.10em', textTransform: 'uppercase',
          color: '#88877f', margin: '0 0 3px',
        }}>
          {label}
        </p>
        <div style={{
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '14.5px', fontWeight: 500,
          color: '#1a1a18', lineHeight: 1.5,
        }}>
          {children}
        </div>
      </div>
    </div>
  )
}

// ── Address card ──────────────────────────────────────────────────────────────
function AddressCard({ address }) {
  const cardStyle = {
    display: 'flex', flexDirection: 'column',
    padding: '32px 28px',
    background: '#ffffff',
    border: '1px solid #e8e8e8',
    textDecoration: 'none',
    transition: 'border-color 0.2s ease, background 0.2s ease',
  }

  const inner = (
    <>
      {/* Map pin icon */}
      <div style={{
        width: '42px', height: '42px', marginBottom: '18px',
        background: '#007935',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="white" strokeWidth="2" strokeLinecap="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>

      {/* Label */}
      <p style={{
        fontFamily: "'DM Sans',sans-serif",
        fontSize: '11px', fontWeight: 700,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: '#007935', margin: '0 0 10px',
      }}>
        {address.label}
      </p>

      {/* Address lines */}
      <p style={{
        fontFamily: "'DM Sans',sans-serif",
        fontSize: '14.5px', fontWeight: 500,
        color: '#1a1a18', lineHeight: 1.6,
        margin: '0 0 12px', flex: 1,
      }}>
        {address.line1}<br/>
        {address.line2}<br/>
        {address.city}, {address.country}
      </p>

      {/* View on map — warehouse only */}
      {address.mapUrl && (
        <span style={{
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '12.5px', fontWeight: 600,
          color: '#007935',
          display: 'inline-flex', alignItems: 'center', gap: '5px',
        }}>
          View on map
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6h8M6 2l4 4-4 4"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </span>
      )}
    </>
  )

  if (address.mapUrl) {
    return (
      <a
        href={address.mapUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={cardStyle}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = '#007935'
          e.currentTarget.style.background  = '#f9f9f7'
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = '#e8e8e8'
          e.currentTarget.style.background  = '#ffffff'
        }}
      >
        {inner}
      </a>
    )
  }

  return <div style={cardStyle}>{inner}</div>
}

// ── Contact Form ──────────────────────────────────────────────────────────────
const SUBJECTS = [
  'General Enquiry',
  'Product Information',
  'Technical Support',
  'Partnership Opportunity',
  'Place an Order',
  'Other',
]

function ContactForm() {
  const [form, setForm]       = useState({ name: '', email: '', phone: '', subject: '', message: '' })
  const [status, setStatus]   = useState('idle')  // 'idle' | 'sending' | 'sent' | 'error'
  const [errors, setErrors]   = useState({})

  const update = (field, val) => {
    setForm(f => ({ ...f, [field]: val }))
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim())    e.name    = 'Name is required'
    if (!form.email.trim())   e.email   = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email'
    if (!form.subject)        e.subject = 'Please select a subject'
    if (!form.message.trim()) e.message = 'Message is required'
    return e
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) { setErrors(errs); return }

    setStatus('sending')
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          access_key: '550dbc06-619b-4aeb-891a-d8359d6682bd',
          name:    form.name,
          email:   form.email,
          phone:   form.phone,
          subject: form.subject,
          message: form.message,
        }),
      })
      const data = await res.json()
      setStatus(data.success ? 'sent' : 'error')
    } catch {
      setStatus('error')
    }
  }

  const inputStyle = (field) => ({
    width: '100%', boxSizing: 'border-box',
    fontFamily: "'DM Sans',sans-serif",
    fontSize: '14px', color: '#1a1a18',
    padding: '11px 14px',
    background: '#ffffff',
    border: `1px solid ${errors[field] ? '#c0392b' : '#e0dfda'}`,
    outline: 'none',
    transition: 'border-color 0.2s ease',
  })

  const labelStyle = {
    display: 'block',
    fontFamily: "'DM Sans',sans-serif",
    fontSize: '12px', fontWeight: 600,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    color: '#55544e', marginBottom: '6px',
  }

  const errorStyle = {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: '11.5px', color: '#c0392b',
    marginTop: '4px', display: 'block',
  }

  if (status === 'sent') {
    return (
      <div style={{
        background: '#f0f9f4', border: '1px solid #007935',
        padding: '32px 28px', textAlign: 'center',
      }}>
        <div style={{
          width: '52px', height: '52px', background: '#007935',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12l5 5L20 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 style={{
          fontFamily: "'DM Sans',sans-serif", fontSize: '1.125rem',
          fontWeight: 600, color: '#007935', margin: '0 0 8px',
        }}>
          Message Sent!
        </h3>
        <p style={{
          fontFamily: "'DM Sans',sans-serif", fontSize: '14px',
          color: '#55544e', margin: '0 0 20px',
        }}>
          Thank you for reaching out. Our team will get back to you within 1–2 business days.
        </p>
        <Button variant="outline-green" onClick={() => { setStatus('idle'); setForm({ name:'', email:'', phone:'', subject:'', message:'' }) }}>
          Send Another Message
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} noValidate>

      {/* Name + Email row */}
      <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Full Name *</label>
          <input
            type="text" value={form.name} placeholder="Your full name"
            onChange={e => update('name', e.target.value)}
            style={inputStyle('name')}
            onFocus={e  => e.target.style.borderColor = '#007935'}
            onBlur={e   => e.target.style.borderColor = errors.name ? '#c0392b' : '#e0dfda'}
          />
          {errors.name && <span style={errorStyle}>{errors.name}</span>}
        </div>
        <div>
          <label style={labelStyle}>Email Address *</label>
          <input
            type="email" value={form.email} placeholder="your@email.com"
            onChange={e => update('email', e.target.value)}
            style={inputStyle('email')}
            onFocus={e  => e.target.style.borderColor = '#007935'}
            onBlur={e   => e.target.style.borderColor = errors.email ? '#c0392b' : '#e0dfda'}
          />
          {errors.email && <span style={errorStyle}>{errors.email}</span>}
        </div>
      </div>

      {/* Phone + Subject row */}
      <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Phone Number</label>
          <input
            type="tel" value={form.phone} placeholder="+94 7xx xxx xxx"
            onChange={e => update('phone', e.target.value)}
            style={inputStyle('phone')}
            onFocus={e  => e.target.style.borderColor = '#007935'}
            onBlur={e   => e.target.style.borderColor = '#e0dfda'}
          />
        </div>
        <div>
          <label style={labelStyle}>Subject *</label>
          <select
            value={form.subject}
            onChange={e => update('subject', e.target.value)}
            style={{ ...inputStyle('subject'), cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' }}
            onFocus={e  => e.target.style.borderColor = '#007935'}
            onBlur={e   => e.target.style.borderColor = errors.subject ? '#c0392b' : '#e0dfda'}
          >
            <option value="">Select a subject...</option>
            {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {errors.subject && <span style={errorStyle}>{errors.subject}</span>}
        </div>
      </div>

      {/* Message */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>Message *</label>
        <textarea
          value={form.message}
          placeholder="Tell us how we can help you..."
          rows={5}
          onChange={e => update('message', e.target.value)}
          style={{ ...inputStyle('message'), resize: 'vertical', minHeight: '120px' }}
          onFocus={e  => e.target.style.borderColor = '#007935'}
          onBlur={e   => e.target.style.borderColor = errors.message ? '#c0392b' : '#e0dfda'}
        />
        {errors.message && <span style={errorStyle}>{errors.message}</span>}
      </div>

      {/* Submit */}
      <Button
        variant="green"
        size="lg"
        loading={status === 'sending'}
        onClick={handleSubmit}
        fullWidth
      >
        {status === 'sending' ? 'Sending...' : 'Send Message'}
      </Button>

      {status === 'error' && (
        <p style={{ ...errorStyle, textAlign: 'center', marginTop: '12px' }}>
          Something went wrong. Please try again or email us directly.
        </p>
      )}

      <style>{`
        @media (max-width: 560px) {
          .form-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </form>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ContactPage() {
  const { addresses, phones, email, socials } = companyInfo

  return (
    <main>
      <SEOHead
        title="Contact Us"
        description="Get in touch with Salient Agrinutrition for product enquiries, technical support, or on-farm consultation. Reach us by phone, WhatsApp, or visit our office in Sri Lanka."
        path="/contact"
      />

      {/* ── 01 PageHero ── */}
      <PageHero
        title="Contact Us"
        subtitle="Get in touch with our team - we're ready to help you find the right nutritional solution."
        breadcrumbs={[
          { label: 'Home',       to: '/' },
          { label: 'Contact Us' },
        ]}
      />

      {/* ── 02 Main contact section: info LEFT + form RIGHT ── */}
      <section style={{ background: '#ffffff', padding: '72px 0' }}>
        <div style={{
          maxWidth: 'var(--container-max)', margin: '0 auto',
          padding:  '0 var(--container-padding)',
        }}>
          <div
            className="contact-main-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1.6fr',
              gap: '72px',
              alignItems: 'start',
            }}
          >

            {/* ── LEFT: contact details ── */}
            <div>
              <h2 style={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 'clamp(1.25rem, 2vw, 1.625rem)',
                fontWeight: 400, letterSpacing: '0.04em', textTransform: 'uppercase',
                color: '#007935', margin: '0 0 28px',
              }}>
                Get in Touch
              </h2>

              {/* Phone numbers */}
              <InfoRow
                label="Call Us"
                icon={
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
                  </svg>
                }
              >
                {phones.map(p => (
                  <a
                    key={p.number}
                    href={`tel:${p.number}`}
                    style={{
                      display: 'block', color: '#1a1a18',
                      textDecoration: 'none', fontWeight: 500,
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#007935'}
                    onMouseLeave={e => e.currentTarget.style.color = '#1a1a18'}
                  >
                    {p.display}
                    {/*<span style={{ fontSize: '11px', color: '#88877f', marginLeft: '6px' }}>
                      ({p.label})
                    </span> */}
                  </a>
                ))}
              </InfoRow>

              {/* Email */}
              <InfoRow
                label="Email Us"
                icon={
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                }
              >
                <a
                  href={`mailto:${email}`}
                  style={{
                    color: '#1a1a18', textDecoration: 'none', fontWeight: 500,
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#007935'}
                  onMouseLeave={e => e.currentTarget.style.color = '#1a1a18'}
                >
                  {email}
                </a>
              </InfoRow>

              {/* Office address */}
              <InfoRow
                label="Office Address"
                icon={
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                }
              >
                <span style={{ color: '#1a1a18', fontWeight: 500 }}>
                  {addresses.office.line1}<br/>
                  {addresses.office.line2}<br/>
                  {addresses.office.city}, {addresses.office.country}
                </span>
              </InfoRow>

              {/* Working hours */}
              <InfoRow
                label="Working Hours"
                icon={
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                }
              >
                <span>Monday - Friday: 8:30 AM - 5:30 PM</span><br/>
                <span style={{ color: '#88877f', fontSize: '13px' }}>Saturday: 9:00 AM – 1:00 PM</span>
              </InfoRow>

              {/* Social media */}
              <div style={{ marginTop: '32px', paddingTop: '28px', borderTop: '1px solid #f0f0ee' }}>
                <p style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: '10.5px', fontWeight: 700,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: '#88877f', margin: '0 0 14px',
                }}>
                  Follow Us
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {socials.map(s => (
                    <a
                      key={s.platform}
                      href={s.href}
                      target="_blank" rel="noopener noreferrer"
                      aria-label={s.label}
                      style={{
                        width: '40px', height: '40px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#f5f5f3', color: '#55544e',
                        textDecoration: 'none',
                        transition: 'background 0.2s ease, color 0.2s ease',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = '#007935'
                        e.currentTarget.style.color      = '#ffffff'
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = '#f5f5f3'
                        e.currentTarget.style.color      = '#55544e'
                      }}
                    >
                      {SOCIAL_ICONS[s.platform]}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* ── RIGHT: Contact form ── */}
            <div>
              <h2 style={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 'clamp(1.25rem, 2vw, 1.625rem)',
                fontWeight: 400, letterSpacing: '0.04em', textTransform: 'uppercase',
                color: '#007935', margin: '0 0 28px',
              }}>
                Send Us a Message
              </h2>
              <ContactForm />
            </div>

          </div>
        </div>
      </section>

      {/* ── 03 Address cards ── */}
      <section style={{ background: '#f5f5f3', padding: '64px 0' }}>
        <div style={{
          maxWidth: 'var(--container-max)', margin: '0 auto',
          padding:  '0 var(--container-padding)',
        }}>
          <h2 style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: 'clamp(1.25rem, 2vw, 1.625rem)',
            fontWeight: 400, letterSpacing: '0.04em', textTransform: 'uppercase',
            color: '#007935', margin: '0 0 32px',
          }}>
            Our Locations
          </h2>

          <div
            className="address-grid"
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: '#e0dfda' }}
          >
            {Object.values(addresses).map(addr => (
              <AddressCard key={addr.label} address={addr} />
            ))}
          </div>
        </div>
      </section>

      {/* ── 04 CtaBanner ── */}
      <CtaBanner
        tag="Let's Work Together"
        title="Ready to improve your herd's performance?"
        body="Our technical team is ready to discuss the right nutritional solution for your specific livestock system."
        buttonText="Call Us Now"
        buttonTo="/contact"
      />

      <style>{`
        @media (max-width: 900px)  { .contact-main-grid { grid-template-columns: 1fr !important; gap: 48px !important; } }
        @media (max-width: 600px)  { .address-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </main>
  )
}